import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import Marquee from "react-fast-marquee";
import { ArrowRight, Target, ChartLineUp, Trophy, Users, CaretRight, Crown } from "@phosphor-icons/react";
import KickoffCountdown from "../components/KickoffCountdown";
import NextThreeKickoffs from "../components/NextThreeKickoffs";
import SignupTicker from "../components/SignupTicker";
import { apiClient } from "../lib/api";
import { teamShort } from "../lib/teamMeta";

const HERO_BG = "https://images.pexels.com/photos/35898730/pexels-photo-35898730.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";
const LEAGUE_BG = "https://images.pexels.com/photos/11953753/pexels-photo-11953753.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";

// Only shown if the API returns zero results (fresh season / DB empty).
// Uses obviously-placeholder syntax so it never masquerades as recent data.
const TICKER_PLACEHOLDER = ["SEASON KICKS OFF SOON", "PREDICT · SCORE · CLIMB"];

const formatCountdown = (kickoffIso) => {
  const target = new Date(kickoffIso).getTime();
  const now = Date.now();
  const diff = target - now;
  if (diff < 0) return "LIVE NOW";
  const hours = Math.floor(diff / (1000 * 60 * 60));
  const mins = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
  if (hours >= 24) return `IN ${Math.floor(hours / 24)}D ${hours % 24}H`;
  if (hours > 0) return `IN ${hours}H ${mins}M`;
  return `IN ${mins}M`;
};

const buildTickerItems = (recent, upcoming) => {
  const results = (recent || []).map((f) => {
    const h = teamShort(f.home_team);
    const a = teamShort(f.away_team);
    return `${h} ${f.home_score}-${f.away_score} ${a}`;
  });
  const kickoffs = (upcoming || []).map((f) => {
    const h = teamShort(f.home_team);
    const a = teamShort(f.away_team);
    return `${h} v ${a} · ${formatCountdown(f.kickoff)}`;
  });
  // Interleave: kickoffs first (imminent), then results. Cap total at 12 to keep
  // marquee readable on mobile.
  const merged = [...kickoffs, ...results].slice(0, 12);
  return merged.length ? merged : TICKER_PLACEHOLDER;
};

export default function Landing() {
  const [tickerItems, setTickerItems] = useState([]);
  useEffect(() => {
    let cancelled = false;
    const load = () =>
      Promise.all([
        apiClient.get("/public/recent-results", { params: { limit: 10 } }).catch(() => ({ data: { results: [] } })),
        apiClient.get("/public/upcoming-fixtures", { params: { limit: 6 } }).catch(() => ({ data: { upcoming: [] } })),
      ]).then(([r, u]) => {
        if (!cancelled) setTickerItems(buildTickerItems(r.data.results, u.data.upcoming));
      });
    load();
    // Refresh every minute so countdowns tick down and finished matches roll in.
    const t = setInterval(load, 60 * 1000);
    return () => { cancelled = true; clearInterval(t); };
  }, []);

  return (
    <div className="text-white">
      <KickoffCountdown variant="banner" />
      <SignupTicker />
      {/* Hero */}
      <section className="relative overflow-hidden">
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: `url(${HERO_BG})` }}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-[#090A0C]/85 via-[#090A0C]/75 to-[#090A0C]" />
        <div className="absolute inset-0 grid-bg opacity-40" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24 sm:py-40">
          <div className="flex items-center gap-3 mb-6 flex-wrap">
            <span className="inline-flex items-center gap-2 px-3 py-1 border border-primary-yellow/30 bg-primary-yellow/10 text-primary-yellow text-[10px] uppercase tracking-[0.25em] font-bold">
              <span className="w-1.5 h-1.5 rounded-full bg-primary-yellow animate-pulse" />
              Premier League · Season 2026/27
            </span>
            <a href="#cup" className="inline-flex items-center gap-2 px-3 py-1 border border-primary-yellow text-primary-yellow text-[10px] uppercase tracking-[0.25em] font-bold hover:bg-primary-yellow hover:text-black transition-colors">
              <Trophy size={12} weight="fill" /> Win the Premier Cup
            </a>
          </div>
          <h1 className="font-heading text-5xl sm:text-7xl lg:text-8xl uppercase font-black leading-[0.95] tracking-tight max-w-4xl">
            Predict the<br />
            <span className="text-primary-yellow">Premiership.</span><br />
            Top the table.
          </h1>
          <p className="mt-8 max-w-xl text-base sm:text-lg text-white/70 leading-relaxed">
            Forecast every fixture. 10 pts for an exact score, 5 for the right result, plus bonuses for nailing a team&apos;s tally. Climb the leaderboard. Run private leagues with mates.
          </p>
          <div className="mt-10 flex flex-wrap items-center gap-4">
            <Link
              data-testid="hero-cta-register"
              to="/register"
              className="group inline-flex items-center gap-2 bg-primary-yellow text-black px-6 py-4 text-sm uppercase tracking-[0.2em] font-bold hover:-translate-y-1 transition-transform shadow-[6px_6px_0_rgba(255,255,255,0.1)]"
            >
              Start Predicting
              <ArrowRight size={16} weight="bold" className="group-hover:translate-x-1 transition-transform" />
            </Link>
            <Link
              data-testid="hero-cta-leaderboard"
              to="/leaderboard"
              className="inline-flex items-center gap-2 border border-white/20 px-6 py-4 text-sm uppercase tracking-[0.2em] font-semibold hover:border-primary-yellow hover:text-primary-yellow transition-colors"
            >
              View Leaderboard
            </Link>
          </div>

          {/* Stat strip */}
          <div className="mt-20 grid grid-cols-2 md:grid-cols-4 gap-px bg-white/10 border border-white/10">
            {[
              { v: "10+", l: "exact score" },
              { v: "+5", l: "correct result" },
              { v: "380", l: "fixtures / season" },
              { v: "Free", l: "to play" },
            ].map((s) => (
              <div key={s.l} className="surface p-6">
                <div className="font-heading text-4xl sm:text-5xl font-black text-primary-yellow font-mono-num">{s.v}</div>
                <div className="text-[10px] uppercase tracking-[0.2em] text-white/50 mt-1">{s.l}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Live ticker */}
      <section className="border-y border-white/10 surface">
        <Marquee gradient={false} speed={45} className="py-3 marquee-container">
          {tickerItems.concat(tickerItems).map((t, i) => (
            <span key={`${t}-${i}`} className="mx-8 font-mono-num text-sm uppercase tracking-widest">
              <span className="text-primary-yellow mr-3">●</span>{t}
            </span>
          ))}
        </Marquee>
      </section>

      {/* Top-3 nearest kickoffs — per-second countdown */}
      <NextThreeKickoffs />

      {/* How it works */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24">
        <div className="grid md:grid-cols-12 gap-12 items-end">
          <div className="md:col-span-5">
            <span className="text-xs uppercase tracking-[0.25em] text-primary-yellow font-bold">The System</span>
            <h2 className="font-heading text-4xl sm:text-5xl uppercase font-black mt-3 leading-none">
              Three steps.<br />Every matchday.
            </h2>
          </div>
          <p className="md:col-span-7 text-white/60 text-lg">
            Built for the obsessives. Lock your predictions before kickoff, get graded after the final whistle, then settle scores on the global table.
          </p>
        </div>
        <div className="mt-16 grid md:grid-cols-3 gap-px bg-white/10 border border-white/10">
          {[
            { icon: Target, title: "01. Predict", desc: "Pick the exact score for every Premier League fixture before kickoff. Save, edit, and lock until the whistle." },
            { icon: ChartLineUp, title: "02. Score", desc: "10 + total goals for an exact score. 5 for the right result. Plus a team's goal count whenever you nail their score — even if the result was wrong. Graded after the final result is posted." },
            { icon: Trophy, title: "03. Climb", desc: "Top the weekly leaderboard. Stack form for the seasonal crown. Compete privately with mates in your own league." },
          ].map((s) => (
            <div key={s.title} className="surface p-8 hover:bg-[#1C1F26] transition-colors group">
              <s.icon size={32} weight="duotone" className="text-primary-yellow" />
              <h3 className="font-heading text-3xl uppercase font-black mt-6">{s.title}</h3>
              <p className="text-white/60 mt-3 text-sm leading-relaxed">{s.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Scoring math card */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-24">
        <div className="grid md:grid-cols-12 gap-8">
          <div className="md:col-span-7 surface border border-white/10 p-10">
            <span className="text-xs uppercase tracking-[0.25em] text-primary-yellow font-bold">Scoring</span>
            <h3 className="font-heading text-3xl sm:text-4xl uppercase font-black mt-3">The Maths</h3>
            <div className="mt-8 space-y-4">
              <ScoringRow predicted="3-3" actual="3-3" points="16" reason="Exact · 10 bonus + 6 goals" />
              <ScoringRow predicted="2-1" actual="2-1" points="13" reason="Exact · 10 bonus + 3 goals" />
              <ScoringRow predicted="2-0" actual="2-1" points="7" reason="Correct home win + home score exact · 5 + 2" />
              <ScoringRow predicted="0-2" actual="1-2" points="7" reason="Correct away win + away score exact · 5 + 2" />
              <ScoringRow predicted="3-1" actual="3-0" points="8" reason="Correct home win + home score exact · 5 + 3" />
              <ScoringRow predicted="2-0" actual="3-1" points="5" reason="Correct result only" />
              <ScoringRow predicted="2-1" actual="2-2" points="2" reason="Wrong result, home score exact · 0 + 2" />
              <ScoringRow predicted="0-2" actual="2-2" points="2" reason="Wrong result, away score exact · 0 + 2" />
              <ScoringRow predicted="2-0" actual="0-2" points="0" reason="Wrong result" />
            </div>
          </div>
          <div
            className="md:col-span-5 relative overflow-hidden border border-white/10"
            style={{ backgroundImage: `url(${LEAGUE_BG})`, backgroundSize: "cover", backgroundPosition: "center" }}
          >
            <div className="absolute inset-0 bg-gradient-to-br from-[#090A0C]/95 via-[#090A0C]/70 to-transparent" />
            <div className="relative p-10 h-full flex flex-col justify-end min-h-[400px]">
              <Users size={40} weight="fill" className="text-primary-yellow" />
              <h3 className="font-heading text-3xl uppercase font-black mt-4">Private Leagues</h3>
              <p className="text-white/70 mt-2 text-sm">Run a season-long battle with mates, colleagues or the fan club. Unlimited members, invite code only, own leaderboard.</p>
              <Link
                data-testid="leagues-cta"
                to="/leagues"
                className="mt-6 inline-flex items-center gap-2 text-primary-yellow text-xs uppercase tracking-[0.2em] font-bold group"
              >
                Create a league <CaretRight size={14} weight="bold" className="group-hover:translate-x-1 transition-transform" />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Premier Cup */}
      <section id="cup" className="relative overflow-hidden border-y border-primary-yellow/20 bg-[#0B0D11]">
        {/* Subtle radial glow behind everything */}
        <div className="absolute inset-0 opacity-40" style={{ background: "radial-gradient(ellipse 80% 60% at 70% 50%, rgba(227,255,0,0.18), transparent 60%)" }} />
        <div className="absolute inset-0" style={{ backgroundImage: "linear-gradient(transparent 95%, rgba(227,255,0,0.05) 95%), linear-gradient(90deg, transparent 95%, rgba(227,255,0,0.05) 95%)", backgroundSize: "40px 40px" }} />

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 md:py-28 grid md:grid-cols-12 gap-12 items-center">
          <div className="md:col-span-7">
            <div className="inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.25em] text-primary-yellow font-bold">
              <Crown size={12} weight="fill" /> The Big Prize
            </div>
            <h2 className="font-heading text-5xl sm:text-6xl md:text-7xl uppercase font-black mt-4 leading-[0.95]">
              Win the<br />
              <span className="text-primary-yellow">Premier Predictors Cup</span>
            </h2>
            <p className="text-white/70 mt-6 text-base sm:text-lg max-w-xl">
              Top the global leaderboard when the final whistle of matchweek 38 blows and your name is etched onto the Premier Cup — immortalised in the Hall of Champions. One winner, one cup, one season.
            </p>
            <div className="mt-8 grid grid-cols-3 gap-px bg-white/10 border border-white/10 max-w-md">
              {[
                { v: "38", l: "Matchweeks" },
                { v: "380", l: "Fixtures" },
                { v: "1", l: "Champion" },
              ].map((s) => (
                <div key={s.l} className="bg-[#0B0D11] py-4 px-3 text-center">
                  <div className="font-mono-num text-3xl font-black text-primary-yellow leading-none">{s.v}</div>
                  <div className="text-[9px] uppercase tracking-[0.22em] text-white/40 mt-2">{s.l}</div>
                </div>
              ))}
            </div>
            <div className="mt-8 flex flex-wrap items-center gap-3">
              <Link
                data-testid="cup-cta-register"
                to="/register"
                className="bg-primary-yellow text-black px-6 py-4 text-xs uppercase tracking-[0.22em] font-bold hover:-translate-y-0.5 transition-transform inline-flex items-center gap-2"
              >
                Enter the race <ArrowRight size={14} weight="bold" />
              </Link>
              <Link
                data-testid="cup-cta-champions"
                to="/champions"
                className="border border-white/15 hover:border-primary-yellow hover:text-primary-yellow text-white/80 px-6 py-4 text-xs uppercase tracking-[0.22em] font-bold transition-colors"
              >
                Past Champions
              </Link>
            </div>
          </div>

          {/* Trophy art panel */}
          <div className="md:col-span-5 relative">
            <div className="relative aspect-[4/5] surface-elevated border border-primary-yellow/30 overflow-hidden">
              {/* Dark gradient backdrop */}
              <div className="absolute inset-0" style={{ background: "radial-gradient(circle at 50% 35%, rgba(227,255,0,0.25), rgba(15,17,22,1) 70%)" }} />
              {/* Corner stamps */}
              <div className="absolute top-5 left-5 right-5 flex items-start justify-between">
                <div>
                  <div className="text-[9px] uppercase tracking-[0.3em] text-primary-yellow font-bold">Season Trophy</div>
                  <div className="text-[10px] uppercase tracking-widest text-white/40 mt-1">Premier Cup</div>
                </div>
                <div className="text-right">
                  <div className="text-[9px] uppercase tracking-[0.3em] text-white/40">2026</div>
                  <div className="font-mono-num text-xs text-white/30">#001</div>
                </div>
              </div>
              {/* Giant trophy with glow */}
              <div className="absolute inset-0 grid place-items-center">
                <div className="relative">
                  <Trophy
                    size={220}
                    weight="fill"
                    className="text-primary-yellow drop-shadow-[0_0_60px_rgba(227,255,0,0.55)]"
                  />
                  {/* Plinth */}
                  <div className="mt-2 mx-auto h-2 w-40 bg-primary-yellow/80" />
                  <div className="mt-1 mx-auto h-1 w-32 bg-primary-yellow/40" />
                  <div className="mt-1 mx-auto h-3 w-48 bg-primary-yellow/20" />
                </div>
              </div>
              {/* Bottom etched name strip */}
              <div className="absolute bottom-5 left-5 right-5 border-t border-primary-yellow/30 pt-4">
                <div className="text-[9px] uppercase tracking-[0.3em] text-white/40">2026/27 Champion</div>
                <div className="font-heading text-xl uppercase font-black text-primary-yellow truncate">Awaits you</div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}

const ScoringRow = ({ predicted, actual, points, reason }) => (
  <div className="flex items-center justify-between border-b border-white/5 pb-4">
    <div className="flex items-center gap-6">
      <div>
        <div className="text-[10px] uppercase tracking-widest text-white/40">Predicted</div>
        <div className="font-heading text-2xl font-black font-mono-num">{predicted}</div>
      </div>
      <div>
        <div className="text-[10px] uppercase tracking-widest text-white/40">Actual</div>
        <div className="font-heading text-2xl font-black font-mono-num">{actual}</div>
      </div>
    </div>
    <div className="text-right">
      <div className="font-mono-num text-3xl font-black text-primary-yellow">+{points}</div>
      <div className="text-[10px] uppercase tracking-widest text-white/50">{reason}</div>
    </div>
  </div>
);
